/**
 * TutorialManager — Tutorial paso a paso con highlight de nodos
 */

import { TutorialStep } from './types';
import { IAM_TUTORIAL } from './tutorial-steps';
import { Patient } from '../medical/Patient';
import state from '../core/state';

export class TutorialManager {
  private container: HTMLElement | null = null;
  private steps: TutorialStep[] = [];
  private currentIndex = 0;
  private patient: Patient | null = null;
  private unsubscribe: (() => void) | null = null;
  private autoTimer: number = 0;
  private pulseInterval: number = 0;
  private sceneManager: any = null;

  constructor() {
    this.injectStyles();
  }

  start(patient: Patient, sceneManager: any, steps?: TutorialStep[]): void {
    this.patient = patient;
    this.sceneManager = sceneManager;
    this.steps = steps || IAM_TUTORIAL;
    this.currentIndex = 0;

    state.setEducationMode('tutorial');
    this.ensureContainer();
    this.renderStep();
  }

  stop(): void {
    this.cleanup();
    this.container?.remove();
    this.container = null;
    state.setEducationMode('none');
  }

  private cleanup(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    clearTimeout(this.autoTimer);
    clearInterval(this.pulseInterval);
  }

  private advance(): void {
    this.cleanup();
    this.currentIndex++;

    if (this.currentIndex >= this.steps.length) {
      state.emit({ type: 'TUTORIAL_COMPLETE' });
      this.stop();
      return;
    }

    state.emit({ type: 'TUTORIAL_ADVANCE', stepIndex: this.currentIndex });
    this.renderStep();
  }

  private renderStep(): void {
    if (!this.container) return;

    const step = this.steps[this.currentIndex];
    const total = this.steps.length;
    const progress = ((this.currentIndex + 1) / total) * 100;

    this.container.innerHTML = `
      <div class="tut-card">
        <div class="tut-progress">
          <div class="tut-progress-fill" style="width: ${progress}%"></div>
        </div>
        <div class="tut-header">
          <span class="tut-step">Paso ${this.currentIndex + 1} de ${total}</span>
          <button class="tut-skip" id="tut-skip">Omitir</button>
        </div>
        <h3 class="tut-instruction">${step.instruction}</h3>
        ${step.detail ? `<p class="tut-detail">${step.detail}</p>` : ''}
        ${step.targetAction === 'click' ? '<p class="tut-hint">&#x1F449; Haz clic en el nodo resaltado</p>' : ''}
        ${step.topologyHint ? '<p class="tut-hint">&#x2328; Presiona la tecla indicada o usa los botones</p>' : ''}
      </div>
    `;

    this.container.querySelector('#tut-skip')!.addEventListener('click', () => this.stop());

    // Highlight nodo target
    if (step.targetNodeId && this.patient) {
      const node = this.patient.getNode(step.targetNodeId);
      if (node) {
        // Pulsar nodo para llamar la atención
        this.pulseInterval = window.setInterval(() => {
          node.pulse();
        }, 1500);

        // Enfocar cámara si se pide
        if (step.focusCamera && this.sceneManager) {
          this.sceneManager.zoomToNode(node.position);
        }
      }
    }

    // Configurar detección de completitud
    if (step.completionEvent) {
      this.unsubscribe = state.onEvent(step.completionEvent as any, (event: any) => {
        if (step.completionNodeId) {
          if (event.nodeId === step.completionNodeId) {
            this.advance();
          }
        } else {
          this.advance();
        }
      });
    }

    // Auto-avance
    if (step.autoAdvanceMs) {
      this.autoTimer = window.setTimeout(() => this.advance(), step.autoAdvanceMs);
    }
  }

  private ensureContainer(): void {
    if (this.container) return;
    this.container = document.createElement('div');
    this.container.id = 'tutorial-container';
    document.body.appendChild(this.container);
  }

  private injectStyles(): void {
    if (document.getElementById('tut-styles')) return;
    const s = document.createElement('style');
    s.id = 'tut-styles';
    s.textContent = `
      #tutorial-container {
        position: fixed;
        bottom: 32px; left: 50%; transform: translateX(-50%);
        z-index: 400;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        pointer-events: none;
      }
      .tut-card {
        pointer-events: auto;
        background: rgba(0,0,0,0.88);
        backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 16px;
        padding: 0 24px 20px;
        width: 460px;
        animation: tutIn 0.4s cubic-bezier(0.25,1,0.5,1);
        overflow: hidden;
      }
      .tut-progress {
        height: 3px; background: rgba(255,255,255,0.06);
        margin: 0 -24px 16px; border-radius: 0;
      }
      .tut-progress-fill {
        height: 100%; background: #2997FF;
        transition: width 0.4s cubic-bezier(0.25,1,0.5,1);
        border-radius: 0 2px 2px 0;
      }
      .tut-header {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 10px;
      }
      .tut-step {
        font-size: 11px; font-weight: 600; color: #6e6e73;
        text-transform: uppercase; letter-spacing: 0.5px;
      }
      .tut-skip {
        font-size: 11px; color: #6e6e73; background: none;
        border: none; cursor: pointer; font-family: inherit;
        padding: 4px 8px; border-radius: 4px;
        transition: all 0.2s;
      }
      .tut-skip:hover { color: #a1a1a6; background: rgba(255,255,255,0.06); }
      .tut-instruction {
        font-size: 17px; font-weight: 700; color: #f5f5f7;
        letter-spacing: -0.3px; margin-bottom: 6px;
      }
      .tut-detail {
        font-size: 13px; color: #a1a1a6; line-height: 1.5;
        margin-bottom: 8px;
      }
      .tut-hint {
        font-size: 12px; color: #2997FF; font-weight: 500;
      }
      @keyframes tutIn {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(s);
  }
}

export const tutorialManager = new TutorialManager();
export default TutorialManager;
